import React from "react";
import SEO from "../SEO";
import config from "../../config";

export default function TheSeaPrivacy() {
  return (
    <>
    <SEO
      title="Privacy Policy — askTheSea"
      description="How askTheSea collects, uses, and protects your information."
      url={`${config.BaseUrl}/privacy`}
      image={`${config.BaseUrl}/assets/logo.png?v=2`}
      noindex
    />
    <section className="page active" id="page-privacy">
      <div className="wrap about-hero">
        <div className="eyebrow">Legal</div>
        <h1>Privacy Policy</h1>
        <p className="lede">Last updated: July 2026</p>
      </div>

      <div className="wrap">
        <div className="legal-content">
          <section className="tight">
            <h2>Overview</h2>
            <p>askTheSea ("we," "us") provides CMC regulatory intelligence derived from publicly available regulatory sources. Our services are not directed to children under 16 years of age. This policy explains what information we collect when you use our site and agent, how we use it, and the choices available to you.</p>
          </section>

          <section className="tight">
            <h2>Information we collect</h2>
            <p>We collect information you provide directly, such as your name and email address when you contact us or interact with our AI agent. We also collect basic usage information, such as pages visited, queries submitted, device and browser information, and information collected through essential cookies and similar technologies, to help us operate and improve the service.</p>
            <p>We retain personal information only as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce our agreements. Different categories of information may be retained for different periods.</p>
          </section>

          <section className="tight">
            <h2>Legal basis for processing (GDPR)</h2>
            <p>If you are located in the European Economic Area (EEA), United Kingdom, or another jurisdiction with similar privacy laws, we process your personal information only when we have a valid legal basis to do so. This includes:</p>
            <ul className="legal-list">
              <li><strong>Providing our service</strong> – to operate the website and "askTheSea" agent, respond to your questions, and provide the information you request.</li>
              <li><strong>Improving and protecting our service</strong> – to maintain security, prevent misuse, troubleshoot issues, and improve the performance of our website and agent.</li>
              <li><strong>Your consent</strong> – where required by law, such as for optional cookies or if you choose to receive marketing communications. You may withdraw your consent at any time.</li>
              <li><strong>Legal obligations</strong> – where we are required to process information to comply with applicable laws or legal requests.</li>
            </ul>
          </section>

          <section className="tight">
            <h2>How we use information</h2>
            <p>We use the information we collect to respond to inquiries, operate and improve the agent and site, monitor for misuse, and communicate with you about updates to our service. We do not sell personal information.</p>
            <p>Information may be processed in the United States and other countries where our service providers operate. Where required by applicable law, we rely on appropriate safeguards for international transfers of personal information.</p>
          </section>

          <section className="tight">
            <h2>Public source material</h2>
            <p>The regulatory content surfaced by askTheSea is drawn exclusively from publicly available sources — agency guidance, notices, meeting minutes, and Q&amp;A documents. Our use of that material is separate from, and does not affect, how we handle your personal information.</p>
          </section>

          <section className="tight">
            <h2>AI model training</h2>
            <p>We do not use your personal information, questions, or conversations with the agent to train, fine-tune, or otherwise improve any AI or machine learning model — ours or any third party's.</p>
          </section>

          <section className="tight">
            <h2>Limited use policy through API providers</h2>
            <p>Where the agent relies on third-party AI API providers to generate responses, we send only the information needed to answer your question. Our agreements with those providers restrict them to processing that information solely to provide the response back to us and prohibit them from using it to train their own models or for any other purpose. Conversation logs may be stored up to 24 months, unless a longer retention period is required by law, to provide the service, investigate misuse, maintain security, and troubleshoot technical issues, but are not used for AI model training.</p>
            <p>We also use single sign-on (SSO) API providers (for example, Google or Microsoft) to let you log in without creating a new password. These providers only share the account details needed to verify your identity and create your account, and we do not authorize them to use that information for any other purpose.</p>
          </section>

          <section className="tight">
            <h2>Data sharing</h2>
            <p>We do not share personal information with third parties except where necessary to operate the service (for example, hosting or analytics providers bound by confidentiality obligations), to comply with the law, or with your consent.</p>
          </section>

          <section className="tight">
            <h2>How do we keep your information safe?</h2>
            <p>We use industry-standard technical and organizational safeguards — including encryption in transit, access controls, and regular review of our systems — to protect your information against unauthorized access, loss, or misuse. No method of transmission or storage is completely secure, but we work to keep these protections current.</p>
          </section>

          <section className="tight">
            <h2>Your rights</h2>
            <p>Depending on where you live, you may have the right to access, correct, delete, or receive a copy of the personal information we hold about you, and to object to or restrict certain processing. You can exercise these rights by contacting us using the details below, and we will respond within the timeframe required by applicable law.</p>
          </section>

          <section className="tight">
            <h2>Your choices</h2>
            <p>You may contact us at any time to ask what information we hold about you, to request a correction, or to request deletion, subject to any legal or operational limitations.</p>
          </section>

          <section className="tight">
            <h2>Changes to this policy</h2>
            <p>We may update this policy from time to time. Material changes will be reflected by an updated "Last updated" date at the top of this page.</p>
          </section>

          <section className="tight">
            <h2>Contact</h2>
            <p>Questions about this policy can be sent to <a href="mailto:info@askthesea.com">info@askthesea.com</a>.</p>
          </section>
        </div>
      </div>
    </section>
    </>
  );
}
